import {
  Theme,
  useMediaQuery,
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmIconButton,
  WhmTypography,
} from "@securitize/reactjs-whm";
import CloseIcon from "@mui/icons-material/Close";
import { useContext, useState } from "react";
import { I18nContext, translationKeys } from "../../contexts/I18nContext";
import { useAcceptAgreementMutation } from "../../data/useAgreements";
import Snackbar, {
  SnackbarContext,
  SnackbarProvider,
} from "../shared/Snackbar";
import ErrorSnackbar from "../shared/ErrorSnackbar";
import { TranslateHtml } from "../Translation/Translate";
import { CustomWhmLoadingButton } from "../shared/CustomWhmLoadingButton";
import { MIXPANEL_EVENTS } from "../../helpers/mixpanel";
import { MixPanelType } from "../../types/mixPanel.types";

export interface BdTermsModalProps {
  investorId: string;
  onAccept: () => void;
  onDismiss: () => void;
  mixPanel?: MixPanelType;
}

function BdTermsModalDialog({
  investorId,
  onAccept,
  onDismiss,
  mixPanel,
}: BdTermsModalProps) {
  const [open, setOpen] = useState(true);
  const { getTranslation: t } = useContext(I18nContext);
  const { showSnackbar } = useContext(SnackbarContext);
  const acceptAgreement = useAcceptAgreementMutation();
  const loading = acceptAgreement.isPending;
  const mobileOrTablet = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xl"),
  );

  const trackEvent = (event: string) => {
    if (!mixPanel) return;
    mixPanel.track(event, {
      investor_id: investorId,
      timestamp: new Date().toISOString(),
    });
  };

  const handleDismiss = () => {
    if (loading) return;
    trackEvent(MIXPANEL_EVENTS.BD_TERMS_MODAL.MODAL_DISMISSED);
    setOpen(false);
    onDismiss();
  };

  const handleAccept = () => {
    if (loading) return;
    acceptAgreement.mutate(
      { type: "bd" },
      {
        onSuccess: () => {
          trackEvent(MIXPANEL_EVENTS.BD_TERMS_MODAL.MODAL_ACCEPTED);
          setOpen(false);
          onAccept();
        },
        onError: (error) => {
          showSnackbar({
            message: <ErrorSnackbar error={error} />,
            severity: "error",
          });
        },
      },
    );
  };

  return (
    <WhmDialog
      data-test-id="bd-terms-modal"
      disablePortal
      open={open}
      onClose={handleDismiss}
      maxWidth={!mobileOrTablet ? "sm" : "lg"}
      fullScreen={mobileOrTablet}
      fullWidth
      aria-labelledby="bd-terms-modal-title"
      aria-describedby="bd-terms-modal-description"
    >
      <WhmDialogTitle
        id="bd-terms-modal-title"
        variant="h4"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {t(translationKeys.BD_TERMS_MODAL_TITLE)}
        <WhmIconButton
          data-test-id="bd-terms-close-button"
          aria-label="close"
          onClick={handleDismiss}
          size="small"
          disabled={loading}
          sx={(theme: Theme) => ({
            color: theme.palette.common.black,
            borderRadius: "50%",
          })}
        >
          <CloseIcon />
        </WhmIconButton>
      </WhmDialogTitle>
      <WhmDialogContent id="bd-terms-modal-description">
        <WhmTypography
          variant="body1"
          component="div"
          sx={{ textAlign: "left", marginTop: "8px" }}
        >
          <TranslateHtml
            translationKey={translationKeys.BD_TERMS_MODAL_DESCRIPTION}
          />
        </WhmTypography>
      </WhmDialogContent>
      <WhmDialogActions
        sx={{
          padding: { xs: 3, sm: 4 },
          display: "flex",
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <WhmButton
          data-test-id="bd-terms-reject-button"
          variant="outlined"
          color="secondary"
          onClick={handleDismiss}
          disabled={loading}
        >
          {t(translationKeys.BD_TERMS_MODAL_REJECT)}
        </WhmButton>
        <CustomWhmLoadingButton
          variant="contained"
          color="primary"
          onClick={handleAccept}
          loading={loading}
        >
          {t(
            loading
              ? translationKeys.BD_TERMS_MODAL_ACCEPT_LOADING
              : translationKeys.BD_TERMS_MODAL_ACCEPT,
          )}
        </CustomWhmLoadingButton>
      </WhmDialogActions>
    </WhmDialog>
  );
}

export default function BdTermsModal(props: BdTermsModalProps) {
  return (
    <SnackbarProvider>
      <BdTermsModalDialog {...props} />
      <Snackbar />
    </SnackbarProvider>
  );
}
